/**
 * @file billPushStatus.js
 * @version 1.1.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-03
 * @modified 2026-10-05
 * @description Where a bill entered here is on its way to Xero, in words — one
 *              rule for Add Bill, the bill window and Search Bills.
 *
 * The server's states (services/billXeroPushService.js, bills.xero_push_status):
 *   draft       left from before 5 October 2026, when drafts waited here;
 *               they are now sent to Xero as drafts like any other bill
 *   queued      saved, waiting to be sent (or sending is switched off)
 *   pushing     being sent right now
 *   sent        in Xero
 *   retrying    Xero was down or busy; the system tries again every 10 minutes
 *   failed      Xero refused it, or something needs fixing first; Retry
 *   local_only  an overpayment / prepayment — never sent from here
 * A bill keyed in Xero (source 'xero') has no state: it came from there.
 *
 * 1.1.0 (2026-10-05): a bill in Xero as a draft (chosen, or Queried) says so.
 * 1.1.1 (2026-10-05, review): a bill voided in Xero for a query whose draft
 * copy failed to go keeps its Retry while sending is switched off.
 */

/**
 * @param {object} b — a bill row or a push-status response: source,
 *        xero_bill_id, xero_push_status | push_status, xero_push_error | error,
 *        xero_push_info | info, sending_enabled (optional)
 * @returns {null | { key: string, label: string, tone: 'success'|'info'|'warning'|'error'|'muted',
 *   icon: string, detail: string|null, canRetry: boolean, mismatches: object[] }}
 */
export function billPushState(b) {
  if (!b) return null;
  const status = b.push_status !== undefined ? b.push_status : b.xero_push_status;
  const error = b.error !== undefined ? b.error : b.xero_push_error;
  const info = (b.info !== undefined ? b.info : b.xero_push_info) || {};
  const xeroId = b.xero_bill_id || null;
  if (b.source && b.source !== 'local' && !status) return null;
  const mismatches = Array.isArray(info?.readback?.mismatches) ? info.readback.mismatches : [];
  // Voided in Xero for a query and its draft copy not there yet: its Retry
  // works even while sending is switched off (server: resendOwed).
  const resendOwed = b.resend_owed === true || (!xeroId && Array.isArray(info.previous_xero_ids) && info.previous_xero_ids.length > 0);
  const off = b.sending_enabled === false && !resendOwed;
  // The bill's own status: a push-status response carries it as bill_status.
  const billStatus = b.bill_status !== undefined ? b.bill_status : b.status;
  const asDraft = String(billStatus || '').toLowerCase() === 'draft';
  const inXeroLabel = asDraft ? 'In Xero as a draft' : 'In Xero';

  const base = { detail: error || null, canRetry: false, mismatches };
  switch (status) {
    case 'sent':
      return {
        ...base,
        key: 'sent',
        label: info?.adopted ? `${inXeroLabel} (matched to the bill already there)` : inXeroLabel,
        tone: mismatches.length || error ? 'warning' : 'success',
        icon: 'cloud_done',
      };
    case 'pushing':
      return { ...base, key: 'pushing', label: 'Sending to Xero…', tone: 'info', icon: 'cloud_sync' };
    case 'retrying':
      return { ...base, key: 'retrying', label: 'Not yet in Xero — trying again every 10 minutes', tone: 'warning', icon: 'cloud_sync', canRetry: !off };
    case 'failed':
      return { ...base, key: 'failed', label: 'Not sent to Xero', tone: 'error', icon: 'cloud_off', canRetry: !off };
    case 'draft':
      // Left from before drafts were sent: it goes with the next send.
      return { ...base, key: 'queued', label: off ? 'Not yet in Xero — sending is switched off' : 'Not yet in Xero (goes as a draft)', tone: 'warning', icon: 'cloud_upload', canRetry: !off };
    case 'local_only':
      return { ...base, key: 'local_only', label: 'Kept here — enter overpayments and prepayments in Xero', tone: 'muted', icon: 'block' };
    case 'queued':
    default:
      if (xeroId) return { ...base, key: 'sent', label: inXeroLabel, tone: 'success', icon: 'cloud_done' };
      return {
        ...base,
        key: off ? 'switched_off' : 'queued',
        label: off ? 'Not yet in Xero — sending is switched off' : 'Not yet in Xero',
        tone: 'warning',
        icon: 'cloud_upload',
        canRetry: !off,
      };
  }
}

/** One line for a read-back disagreement: "total: ours £544.57, Xero's £544.58". */
export function mismatchText(m) {
  if (!m) return '';
  const fmt = (v) => (typeof v === 'number' ? `£${v.toFixed(2)}` : (v ?? '—'));
  return `${String(m.field).replace(/_/g, ' ')}: ours ${fmt(m.ours)}, Xero's ${fmt(m.xeros)}`;
}
